// src/pages/Templates/Template-Administrativo/Governanca/Certidoes-e-Documentos/DocumentoDetailSidePanel.tsx

import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Clock3,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  Paperclip,
  UploadCloud,
  X,
} from "lucide-react";

import { useAuth } from "../../../../../context/auth-context";

type StatusValidade = "regular" | "a_vencer" | "vencido" | "sem_validade";

type Periodicidade =
  | "semanal"
  | "mensal"
  | "trimestral"
  | "semestral"
  | "anual"
  | "sem_validade"
  | "sob_demanda";

type TipoValidade =
  | "com_vencimento"
  | "sem_vencimento"
  | "sob_demanda"
  | "revisao_periodica";

type OrigemSolicitacao =
  | "cliente"
  | "portal_cliente"
  | "email_cliente"
  | "fiscalizacao"
  | "contador"
  | "interno"
  | "outro";

type ProvidenciaStatus =
  | "nao_aplicavel"
  | "nao_iniciado"
  | "sem_agendamento"
  | "agendada"
  | "vistoria_tecnica_agendada"
  | "comparecimento_presencial"
  | "aguardando_documentos"
  | "aguardando_cliente"
  | "em_emissao"
  | "em_renovacao"
  | "emitido"
  | "concluida";

type ProvidenciaTipoAtendimento =
  | "nao_definido"
  | "online"
  | "email"
  | "presencial"
  | "vistoria_tecnica"
  | "contador"
  | "telefone"
  | "outro";

type RastreabilidadeStatus =
  | "vigente"
  | "substituido"
  | "vencido"
  | "aguardando_validacao"
  | "cancelado";

type PortalStatusOperacional =
  | "atualizado"
  | "pendente_anexo"
  | "renovacao_pendente"
  | "vencido"
  | "aguardando_validacao"
  | "nao_exigido"
  | string;

type PortalResumo = {
  totalExigem: number;
  anexadosAtualizados: number;
  pendenteAnexo: number;
  vencidos: number;
  renovacaoPendente: number;
  aguardandoValidacao: number;
};

type OperacionalVisual = {
  key:
    | "ok"
    | "pendente_anexo"
    | "vencida"
    | "renovacao_pendente"
    | "aguardando_validacao"
    | "pendente"
    | "nao_exigido";
  label: string;
  background: string;
  color: string;
  border: string;
};

type DocumentoApi = {
  id: number | string;
  empresaId?: number | string | null;
  empresa_id?: number | string | null;
  moduloId?: number | string | null;
  modulo_id?: number | string | null;

  nome?: string | null;
  categoria?: string | null;
  orgaoEmissor?: string | null;
  orgao_emissor?: string | null;

  tipoValidade?: string | null;
  tipo_validade?: string | null;
  periodicidade?: string | null;

  areaResponsavel?: string | null;
  area_responsavel?: string | null;
  responsavelNome?: string | null;
  responsavel_nome?: string | null;

  arquivoVigenteId?: number | string | null;
  arquivo_vigente_id?: number | string | null;
  arquivoNome?: string | null;
  arquivo_nome?: string | null;
  s3Bucket?: string | null;
  s3_bucket?: string | null;
  s3Key?: string | null;
  s3_key?: string | null;

  dataEmissao?: string | null;
  data_emissao?: string | null;
  dataValidade?: string | null;
  data_validade?: string | null;
  dataUpload?: string | null;
  data_upload?: string | null;

  statusValidade?: string | null;
  status_validade?: string | null;
  anexoDisponivel?: boolean | null;
  anexo_disponivel?: boolean | null;

  documentoPossuido?: boolean | null;
  documento_possuido?: boolean | null;

  origemSolicitacao?: string | null;
  origem_solicitacao?: string | null;
  clienteSolicitante?: string | null;
  cliente_solicitante?: string | null;
  motivoExigencia?: string | null;
  motivo_exigencia?: string | null;

  providenciaStatus?: string | null;
  providencia_status?: string | null;
  providenciaAgendadaPara?: string | null;
  providencia_agendada_para?: string | null;
  providenciaTipoAtendimento?: string | null;
  providencia_tipo_atendimento?: string | null;
  providenciaLocal?: string | null;
  providencia_local?: string | null;
  providenciaDocumentosNecessarios?: string | null;
  providencia_documentos_necessarios?: string | null;
  providenciaObservacao?: string | null;
  providencia_observacao?: string | null;

  emissaoStatus?: string | null;
  emissao_status?: string | null;
  emissaoAgendadaPara?: string | null;
  emissao_agendada_para?: string | null;
  emissaoTipoAtendimento?: string | null;
  emissao_tipo_atendimento?: string | null;
  emissaoLocal?: string | null;
  emissao_local?: string | null;
  emissaoDocumentosNecessarios?: string | null;
  emissao_documentos_necessarios?: string | null;
  emissaoObservacao?: string | null;
  emissao_observacao?: string | null;

  renovacaoStatus?: string | null;
  renovacao_status?: string | null;
  renovacaoAgendadaPara?: string | null;
  renovacao_agendada_para?: string | null;
  renovacaoTipoAtendimento?: string | null;
  renovacao_tipo_atendimento?: string | null;
  renovacaoLocal?: string | null;
  renovacao_local?: string | null;
  renovacaoDocumentosNecessarios?: string | null;
  renovacao_documentos_necessarios?: string | null;
  renovacaoObservacao?: string | null;
  renovacao_observacao?: string | null;

  observacao?: string | null;
  descricao?: string | null;

  portais?: {
    totalExigem?: number | string | null;
    anexadosAtualizados?: number | string | null;
    pendenteAnexo?: number | string | null;
    renovacaoPendente?: number | string | null;
    emRenovacao?: number | string | null;
    vencidos?: number | string | null;
    aguardandoValidacao?: number | string | null;
  } | null;
};

type DocumentoArquivoApi = {
  id: number | string;
  documentoId?: number | string | null;
  documento_id?: number | string | null;
  empresaId?: number | string | null;
  empresa_id?: number | string | null;
  moduloId?: number | string | null;
  modulo_id?: number | string | null;
  versao?: number | string | null;
  vigente?: boolean | null;
  obsoleto?: boolean | null;
  dataEmissao?: string | null;
  data_emissao?: string | null;
  dataValidade?: string | null;
  data_validade?: string | null;
  s3Bucket?: string | null;
  s3_bucket?: string | null;
  s3Key?: string | null;
  s3_key?: string | null;
  mimeType?: string | null;
  mime_type?: string | null;
  originalName?: string | null;
  original_name?: string | null;
  sizeBytes?: number | string | null;
  size_bytes?: number | string | null;
  observacao?: string | null;
  criadoEm?: string | null;
  criado_em?: string | null;
  criadoPor?: number | string | null;
  criado_por?: number | string | null;
  substituidoEm?: string | null;
  substituido_em?: string | null;
  substituidoPor?: number | string | null;
  substituido_por?: number | string | null;
};

type DocumentoPortalApi = {
  id: number | string;
  documentoId?: number | string | null;
  documento_id?: number | string | null;
  empresaClienteId?: number | string | null;
  empresa_cliente_id?: number | string | null;
  empresaClientePortalId?: number | string | null;
  empresa_cliente_portal_id?: number | string | null;
  clienteNome?: string | null;
  cliente_nome?: string | null;
  portalNome?: string | null;
  portal_nome?: string | null;
  portalUrl?: string | null;
  portal_url?: string | null;
  exigeDocumento?: boolean | null;
  exige_documento?: boolean | null;
  statusOperacional?: PortalStatusOperacional | null;
  status_operacional?: PortalStatusOperacional | null;
  arquivoId?: number | string | null;
  arquivo_id?: number | string | null;
  atualizadoEm?: string | null;
  atualizado_em?: string | null;
  observacao?: string | null;
};

type ApiDetalheResponse = {
  ok?: boolean;
  documento?: DocumentoApi | null;
  arquivoVigente?: DocumentoArquivoApi | null;
  arquivo_vigente?: DocumentoArquivoApi | null;
  rastreabilidade?: DocumentoArquivoApi[];
  portais?: DocumentoPortalApi[];
  erro?: string;
};

type ApiPresignUploadResponse = {
  ok?: boolean;
  bucket?: string;
  key?: string;
  uploadUrl?: string;
  upload_url?: string;
  expiresIn?: number;
  expires_in?: number;
  mimeType?: string;
  mime_type?: string;
  originalName?: string;
  original_name?: string;
  sizeBytes?: number;
  size_bytes?: number;
  erro?: string;
};

type ApiRegistrarAnexoResponse = {
  ok?: boolean;
  arquivo?: DocumentoArquivoApi;
  erro?: string;
};

type ApiAnexoUrlResponse = {
  ok?: boolean;
  url?: string;
  expiresIn?: number;
  expires_in?: number;
  arquivo?: {
    id: number;
    documentoId?: number;
    documento_id?: number;
    originalName?: string;
    original_name?: string;
    mimeType?: string;
    mime_type?: string;
  };
  erro?: string;
};

type DocumentoDetalhe = {
  id: number;
  nome: string;
  categoria: string;
  orgaoEmissor: string;
  empresa: string;
  cnpj: string;

  documentoPossuido: boolean;
  origemSolicitacao: OrigemSolicitacao;
  clienteSolicitante: string | null;
  motivoExigencia: string | null;

  dataEmissao: string | null;
  dataValidade: string | null;
  dataUpload: string | null;

  tipoValidade: TipoValidade;
  periodicidade: Periodicidade;

  areaResponsavel: string;
  responsavelNome?: string | null;

  statusValidade: StatusValidade;

  providenciaStatus: ProvidenciaStatus;
  providenciaAgendadaPara: string | null;
  providenciaTipoAtendimento: ProvidenciaTipoAtendimento;
  providenciaLocal: string | null;
  providenciaDocumentosNecessarios: string | null;
  providenciaObservacao: string | null;

  anexoDisponivel: boolean;
  arquivoVigenteId?: number | null;
  arquivoNome?: string | null;
  s3Key?: string | null;

  observacao?: string | null;
  portais: PortalResumo;
};

type DocumentoVersao = {
  id: number;
  documentoId: number;
  versao: number;
  arquivoNome: string;
  s3Bucket: string | null;
  s3Key: string;
  mimeType: string | null;
  dataEmissao: string | null;
  dataValidade: string | null;
  dataUpload: string;
  responsavelUpload: string;
  vigente: boolean;
  obsoleto: boolean;
  observacao?: string | null;
};

type DocumentoRastreabilidade = DocumentoVersao & {
  statusRastreabilidade: RastreabilidadeStatus;
  titulo: string;
  descricaoStatus: string;
};

type ViewerState = {
  open: boolean;
  loading: boolean;
  url: string | null;
  mimeType: string | null;
  fileName: string;
  error: string | null;
};

type DocumentoDetailSidePanelProps = {
  open: boolean;
  documentoId: number | string | null;
  departmentColor?: string;
  onClose: () => void;
  onAfterChange?: () => void;
};

const API_BASE = "https://api.logvi-havox.com";
const API_DOCUMENTOS_URL = `${API_BASE}/template/governanca/documentos`;
const API_PRESIGN_UPLOAD_URL = `${API_BASE}/template/governanca/documentos/anexos/presign-upload`;
const API_REGISTRAR_ANEXO_URL = `${API_BASE}/template/governanca/documentos/anexos/registrar`;

const DEPARTMENT_COLOR = "#2f3f59";
const HEADER_HEIGHT = 52;
const LEFTBAR_WIDTH = 64;
const MOBILE_BREAKPOINT = 900;

const initialViewerState: ViewerState = {
  open: false,
  loading: false,
  url: null,
  mimeType: null,
  fileName: "",
  error: null,
};

function normalizeText(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function toNumber(value: unknown, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toNumberOrNull(value: unknown): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toNullableString(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  return str ? str : null;
}

function normalizeApiDate(value: string | null | undefined): string | null {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;

  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDate) {
    const [, year, month, day] = isoDate;
    return `${day}/${month}/${year}`;
  }

  return raw;
}

function normalizeApiDateTime(value: string | null | undefined): string {
  if (!value) return "Não informado";

  const raw = String(value).trim();
  if (!raw) return "Não informado";

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);

  if (iso) {
    const [, yyyy, mm, dd, hh, min] = iso;
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  return normalizeApiDate(raw) || raw;
}

function parseDateFlexible(value: string | null): Date | null {
  if (!value) return null;

  const raw = String(value).trim();

  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (br) {
    const [, dd, mm, yyyy] = br;
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, yyyy, mm, dd] = iso;
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getDaysDiffFromToday(date: Date | null) {
  if (!date) return null;

  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const diffMs = target.getTime() - today.getTime();

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatMesesDias(totalDias: number) {
  const diasAbsolutos = Math.abs(totalDias);
  if (diasAbsolutos < 30) return null;

  const meses = Math.floor(diasAbsolutos / 30);
  const dias = diasAbsolutos % 30;

  const mesesTexto = meses === 1 ? "1 mês" : `${meses} meses`;
  const diasTexto = dias === 1 ? "1 dia" : `${dias} dias`;

  if (dias === 0) return mesesTexto;

  return `${mesesTexto} e ${diasTexto}`;
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function normalizeTipoValidade(value: unknown): TipoValidade {
  const raw = String(value || "").trim();

  if (
    raw === "com_vencimento" ||
    raw === "sem_vencimento" ||
    raw === "sob_demanda" ||
    raw === "revisao_periodica"
  ) {
    return raw;
  }

  return "com_vencimento";
}

function normalizePeriodicidade(value: unknown): Periodicidade {
  const raw = String(value || "").trim();

  if (
    raw === "semanal" ||
    raw === "mensal" ||
    raw === "trimestral" ||
    raw === "semestral" ||
    raw === "anual" ||
    raw === "sem_validade" ||
    raw === "sob_demanda"
  ) {
    return raw;
  }

  return "sob_demanda";
}

function normalizeStatusValidade(value: unknown): StatusValidade {
  const raw = String(value || "").trim();

  if (
    raw === "regular" ||
    raw === "a_vencer" ||
    raw === "vencido" ||
    raw === "sem_validade"
  ) {
    return raw;
  }

  return "sem_validade";
}

function normalizeOrigemSolicitacao(value: unknown): OrigemSolicitacao {
  const raw = String(value || "").trim();

  if (
    raw === "cliente" ||
    raw === "portal_cliente" ||
    raw === "email_cliente" ||
    raw === "fiscalizacao" ||
    raw === "contador" ||
    raw === "interno" ||
    raw === "outro"
  ) {
    return raw;
  }

  return "interno";
}

function normalizeProvidenciaStatus(value: unknown): ProvidenciaStatus {
  const raw = String(value || "").trim();

  if (
    raw === "nao_aplicavel" ||
    raw === "nao_iniciado" ||
    raw === "sem_agendamento" ||
    raw === "agendada" ||
    raw === "vistoria_tecnica_agendada" ||
    raw === "comparecimento_presencial" ||
    raw === "aguardando_documentos" ||
    raw === "aguardando_cliente" ||
    raw === "em_emissao" ||
    raw === "em_renovacao" ||
    raw === "emitido" ||
    raw === "concluida"
  ) {
    return raw;
  }

  return "sem_agendamento";
}

function normalizeProvidenciaTipoAtendimento(
  value: unknown
): ProvidenciaTipoAtendimento {
  const raw = String(value || "").trim();

  if (
    raw === "nao_definido" ||
    raw === "online" ||
    raw === "email" ||
    raw === "presencial" ||
    raw === "vistoria_tecnica" ||
    raw === "contador" ||
    raw === "telefone" ||
    raw === "outro"
  ) {
    return raw;
  }

  return "nao_definido";
}

function getEmpresaIdFromSessao(sessao: any): number | null {
  const value =
    sessao?.empresa_id ??
    sessao?.empresaId ??
    sessao?.empresa_atual_id ??
    sessao?.empresaSelecionadaId ??
    sessao?.empresa?.id;

  if (value === undefined || value === null || value === "") return null;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getModuloIdFromSessao(sessao: any): number | null {
  const direct =
    sessao?.modulo_id ??
    sessao?.moduloId ??
    sessao?.modulo_atual_id ??
    sessao?.moduloSelecionadoId ??
    sessao?.modulo?.id;

  if (direct !== undefined && direct !== null && direct !== "") {
    const n = Number(direct);
    return Number.isFinite(n) ? n : null;
  }

  const isGovernancaDepartment = (dep: any) => {
    const depSlug = normalizeText(dep?.slug);
    const depNome = normalizeText(dep?.nome);
    const depRouteKey = normalizeText(dep?.routeKey ?? dep?.route_key);
    const depCodigo = normalizeText(dep?.codigo);

    return (
      depSlug.includes("governanca") ||
      depSlug.includes("governança") ||
      depNome.includes("governanca") ||
      depNome.includes("governança") ||
      depNome.includes("compliance") ||
      depRouteKey.includes("governanca") ||
      depRouteKey.includes("governança") ||
      depCodigo.includes("governanca") ||
      depCodigo.includes("governança")
    );
  };

  const isCertidoesModule = (mod: any) => {
    const id = mod?.id;
    if (id === undefined || id === null || id === "") return false;

    const full = [
      mod?.codigo,
      mod?.label,
      mod?.nome,
      mod?.routeKey,
      mod?.route_key,
      mod?.actionKey,
      mod?.action_key,
    ]
      .map(normalizeText)
      .join(" ");

    return (
      full.includes("certidoes") ||
      full.includes("certidões") ||
      full.includes("documentos") ||
      full.includes("certidoes-e-documentos") ||
      full.includes("certidoes_documentos")
    );
  };

  const extractFromDepartments = (departamentos: any[]): number | null => {
    for (const dep of departamentos) {
      if (!isGovernancaDepartment(dep)) continue;

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];
      const moduloCertidoes =
        modulos.find((mod: any) => isCertidoesModule(mod)) || modulos[0];

      const id = moduloCertidoes?.id;

      if (id !== undefined && id !== null && id !== "") {
        const n = Number(id);
        if (Number.isFinite(n)) return n;
      }
    }

    return null;
  };

  const departamentosDiretos = Array.isArray(sessao?.departamentos_acessiveis)
    ? sessao.departamentos_acessiveis
    : [];

  const fromDepartamentosDiretos = extractFromDepartments(departamentosDiretos);
  if (fromDepartamentosDiretos) return fromDepartamentosDiretos;

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosComModulos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  if (enderecoSelecionadoId) {
    const enderecoSelecionado = enderecosComModulos.find(
      (end: any) => String(end?.id ?? "") === enderecoSelecionadoId
    );

    const departamentosEndereco = Array.isArray(
      enderecoSelecionado?.departamentos_acessiveis
    )
      ? enderecoSelecionado.departamentos_acessiveis
      : [];

    const fromEnderecoSelecionado = extractFromDepartments(
      departamentosEndereco
    );

    if (fromEnderecoSelecionado) return fromEnderecoSelecionado;
  }

  for (const endereco of enderecosComModulos) {
    const departamentosEndereco = Array.isArray(
      endereco?.departamentos_acessiveis
    )
      ? endereco.departamentos_acessiveis
      : [];

    const fromEndereco = extractFromDepartments(departamentosEndereco);
    if (fromEndereco) return fromEndereco;
  }

  return null;
}

function pluralPortal(qtd: number) {
  return qtd === 1 ? "canal" : "canais";
}

function formatNullableDate(value: string | null | undefined) {
  return normalizeApiDate(value) || "Não informado";
}

function getTipoValidadeLabel(tipo: TipoValidade) {
  switch (tipo) {
    case "com_vencimento":
      return "Com vencimento";
    case "sem_vencimento":
      return "Sem vencimento";
    case "sob_demanda":
      return "Sob demanda";
    case "revisao_periodica":
      return "Revisão periódica";
    default:
      return "Indefinido";
  }
}

function getPeriodicidadeLabel(periodicidade: Periodicidade) {
  switch (periodicidade) {
    case "semanal":
      return "Semanal";
    case "mensal":
      return "Mensal";
    case "trimestral":
      return "Trimestral";
    case "semestral":
      return "Semestral";
    case "anual":
      return "Anual";
    case "sem_validade":
      return "Sem validade";
    case "sob_demanda":
      return "Sob demanda";
    default:
      return "Indefinida";
  }
}

function getStatusValidadeLabel(status: StatusValidade) {
  switch (status) {
    case "regular":
      return "Regular";
    case "a_vencer":
      return "A vencer";
    case "vencido":
      return "Vencida";
    case "sem_validade":
      return "Sem validade";
    default:
      return "Indefinido";
  }
}

function getOrigemSolicitacaoLabel(origem: OrigemSolicitacao) {
  switch (origem) {
    case "cliente":
      return "Cliente";
    case "portal_cliente":
      return "Portal do cliente";
    case "email_cliente":
      return "E-mail do cliente";
    case "fiscalizacao":
      return "Fiscalização";
    case "contador":
      return "Contador";
    case "interno":
      return "Interno";
    case "outro":
      return "Outro";
    default:
      return "Não informada";
  }
}

function getProvidenciaStatusLabel(
  status: ProvidenciaStatus,
  documentoPossuido: boolean
) {
  if (!documentoPossuido) {
    switch (status) {
      case "nao_iniciado":
      case "sem_agendamento":
        return "Não iniciado";
      case "vistoria_tecnica_agendada":
        return "Vistoria técnica agendada";
      case "agendada":
        return "Emissão agendada";
      case "comparecimento_presencial":
        return "Comparecimento presencial";
      case "aguardando_documentos":
        return "Aguardando documentos";
      case "aguardando_cliente":
        return "Aguardando cliente";
      case "em_emissao":
      case "em_renovacao":
        return "Em emissão";
      case "emitido":
      case "concluida":
        return "Emitido";
      case "nao_aplicavel":
        return "Não aplicável";
      default:
        return "Não iniciado";
    }
  }

  switch (status) {
    case "nao_aplicavel":
      return "Não aplicável";
    case "nao_iniciado":
    case "sem_agendamento":
      return "Sem agendamento";
    case "agendada":
      return "Renovação agendada";
    case "vistoria_tecnica_agendada":
      return "Vistoria técnica agendada";
    case "comparecimento_presencial":
      return "Comparecimento presencial";
    case "aguardando_documentos":
      return "Aguardando documentos";
    case "aguardando_cliente":
      return "Aguardando cliente";
    case "em_emissao":
    case "em_renovacao":
      return "Em renovação";
    case "emitido":
    case "concluida":
      return "Concluída";
    default:
      return "Sem agendamento";
  }
}

function getProvidenciaTipoAtendimentoLabel(tipo: ProvidenciaTipoAtendimento) {
  switch (tipo) {
    case "nao_definido":
      return "Não definido";
    case "online":
      return "Online";
    case "email":
      return "E-mail";
    case "presencial":
      return "Presencial";
    case "vistoria_tecnica":
      return "Vistoria técnica";
    case "contador":
      return "Contador";
    case "telefone":
      return "Telefone";
    case "outro":
      return "Outro";
    default:
      return "Não definido";
  }
}

function getPrazoOperacional(item: DocumentoDetalhe) {
  if (!item.documentoPossuido) return "Documento ainda não emitido";

  if (item.tipoValidade === "sem_vencimento") return "Sem vencimento";
  if (item.tipoValidade === "sob_demanda") return "Sob demanda";

  if (item.tipoValidade === "revisao_periodica" && !item.dataValidade) {
    return "Revisão periódica";
  }

  const validade = parseDateFlexible(item.dataValidade);
  const dias = getDaysDiffFromToday(validade);

  if (dias === null) return "Validade não informada";

  const mesesDias = formatMesesDias(dias);
  const complemento = mesesDias ? ` (${mesesDias})` : "";

  if (dias > 1) return `Faltam ${dias} dias${complemento}`;
  if (dias === 1) return "Falta 1 dia";
  if (dias === 0) return "Vence hoje";
  if (dias === -1) return "Vencida há 1 dia";

  return `Vencida há ${Math.abs(dias)} dias${complemento}`;
}

function getOperacionalVisual(portais: PortalResumo): OperacionalVisual {
  const total = portais.totalExigem || 0;
  const atualizados = portais.anexadosAtualizados || 0;

  if (total <= 0) {
    return {
      key: "nao_exigido",
      label: "Não exigido em canais",
      background: "#f8fafc",
      color: "#475569",
      border: "#cbd5e1",
    };
  }

  if (portais.vencidos > 0) {
    return {
      key: "vencida",
      label: `Vencida em ${portais.vencidos} ${pluralPortal(portais.vencidos)}`,
      background: "#fef2f2",
      color: "#991b1b",
      border: "#fecaca",
    };
  }

  if (portais.pendenteAnexo > 0) {
    return {
      key: "pendente_anexo",
      label: `Pendente anexo em ${portais.pendenteAnexo} ${pluralPortal(
        portais.pendenteAnexo
      )}`,
      background: "#fff7ed",
      color: "#9a3412",
      border: "#fed7aa",
    };
  }

  if (portais.renovacaoPendente > 0) {
    return {
      key: "renovacao_pendente",
      label: `Renovação pendente em ${portais.renovacaoPendente} ${pluralPortal(
        portais.renovacaoPendente
      )}`,
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "#bfdbfe",
    };
  }

  if (portais.aguardandoValidacao > 0) {
    return {
      key: "aguardando_validacao",
      label: `Aguardando validação em ${
        portais.aguardandoValidacao
      } ${pluralPortal(portais.aguardandoValidacao)}`,
      background: "#f5f3ff",
      color: "#6d28d9",
      border: "#ddd6fe",
    };
  }

  if (atualizados >= total) {
    return {
      key: "ok",
      label: `Atualizado em ${total} ${pluralPortal(total)}`,
      background: "#ecfdf3",
      color: "#166534",
      border: "#bbf7d0",
    };
  }

  const pendentesCalculados = Math.max(total - atualizados, 0);

  return {
    key: "pendente",
    label: `Pendente em ${pendentesCalculados} ${pluralPortal(
      pendentesCalculados
    )}`,
    background: "#fffbeb",
    color: "#92400e",
    border: "#fde68a",
  };
}

function getStatusValidadeVisual(status: StatusValidade) {
  switch (status) {
    case "regular":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };
    case "a_vencer":
      return {
        background: "#fffbeb",
        color: "#92400e",
        border: "#fde68a",
        icon: <Clock3 size={12} />,
      };
    case "vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <AlertTriangle size={12} />,
      };
    case "sem_validade":
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };
  }
}

function getProvidenciaVisual(status: ProvidenciaStatus) {
  switch (status) {
    case "agendada":
    case "vistoria_tecnica_agendada":
    case "comparecimento_presencial":
      return {
        background: "#eff6ff",
        color: "#1d4ed8",
        border: "#bfdbfe",
      };
    case "aguardando_documentos":
    case "aguardando_cliente":
    case "em_emissao":
    case "em_renovacao":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
      };
    case "emitido":
    case "concluida":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
      };
    case "nao_iniciado":
    case "nao_aplicavel":
    case "sem_agendamento":
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
      };
  }
}

function getPortalStatusLabel(status: PortalStatusOperacional | null | undefined) {
  switch (status) {
    case "atualizado":
      return "Atualizado";
    case "pendente_anexo":
      return "Pendente anexo";
    case "renovacao_pendente":
      return "Renovação pendente";
    case "vencido":
      return "Vencido";
    case "aguardando_validacao":
      return "Aguardando validação";
    case "nao_exigido":
      return "Não exigido";
    default:
      return "Não informado";
  }
}

function getPortalStatusVisual(status: PortalStatusOperacional | null | undefined) {
  switch (status) {
    case "atualizado":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
      };
    case "vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
      };
    case "pendente_anexo":
    case "renovacao_pendente":
      return {
        background: "#fff7ed",
        color: "#9a3412",
        border: "#fed7aa",
      };
    case "aguardando_validacao":
      return {
        background: "#f5f3ff",
        color: "#6d28d9",
        border: "#ddd6fe",
      };
    case "nao_exigido":
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
      };
  }
}

function getRastreabilidadeStatus(
  versao: DocumentoVersao,
  documento: DocumentoDetalhe
): RastreabilidadeStatus {
  if (versao.vigente) return "vigente";
  if (versao.obsoleto) return "substituido";

  const validade = parseDateFlexible(versao.dataValidade || documento.dataValidade);
  const dias = getDaysDiffFromToday(validade);

  if (dias !== null && dias < 0) return "vencido";

  return "substituido";
}

function getRastreabilidadeLabel(status: RastreabilidadeStatus) {
  switch (status) {
    case "vigente":
      return "Vigente";
    case "substituido":
      return "Substituído";
    case "vencido":
      return "Vencido";
    case "aguardando_validacao":
      return "Aguardando validação";
    case "cancelado":
      return "Cancelado";
    default:
      return "Registro";
  }
}

function getRastreabilidadeDescricao(
  status: RastreabilidadeStatus,
  versao: DocumentoVersao
) {
  switch (status) {
    case "vigente":
      return "Arquivo atualmente utilizado como evidência vigente do documento.";
    case "substituido":
      return "Arquivo mantido para rastreabilidade, mas substituído por versão mais recente.";
    case "vencido":
      return "Arquivo com validade expirada, mantido para histórico.";
    case "aguardando_validacao":
      return "Arquivo anexado e aguardando validação operacional.";
    case "cancelado":
      return "Registro cancelado.";
    default:
      return versao.observacao || "Registro documental.";
  }
}

function getRastreabilidadeVisual(status: RastreabilidadeStatus) {
  switch (status) {
    case "vigente":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#86efac",
      };
    case "vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
      };
    case "aguardando_validacao":
      return {
        background: "#f5f3ff",
        color: "#6d28d9",
        border: "#ddd6fe",
      };
    case "substituido":
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
      };
  }
}

function guessMimeTypeFromName(fileName: string, fallback?: string | null) {
  if (fallback) return fallback;

  const lower = String(fileName || "").toLowerCase();

  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".gif")) return "image/gif";

  return "application/octet-stream";
}

function mapDocumentoApi(
  item: DocumentoApi,
  empresaNome: string,
  empresaCnpj: string
): DocumentoDetalhe {
  const documentoPossuido =
    item.documentoPossuido ?? item.documento_possuido ?? true;

  const arquivoVigenteId = toNumberOrNull(
    item.arquivoVigenteId ?? item.arquivo_vigente_id
  );

  const dataEmissao = normalizeApiDate(item.dataEmissao ?? item.data_emissao);
  const dataValidade = normalizeApiDate(item.dataValidade ?? item.data_validade);
  const dataUpload = normalizeApiDateTime(item.dataUpload ?? item.data_upload);

  const portaisRaw = item.portais || {};

  const renovacaoPendente =
    toNumber(portaisRaw.renovacaoPendente, 0) ||
    toNumber(portaisRaw.emRenovacao, 0);

  const documento: DocumentoDetalhe = {
    id: toNumber(item.id),
    nome: toNullableString(item.nome) || "Documento sem nome",
    categoria: toNullableString(item.categoria) || "Sem categoria",
    orgaoEmissor:
      toNullableString(item.orgaoEmissor ?? item.orgao_emissor) ||
      "Não informado",
    empresa: empresaNome,
    cnpj: empresaCnpj,

    documentoPossuido,
    origemSolicitacao: normalizeOrigemSolicitacao(
      item.origemSolicitacao ?? item.origem_solicitacao
    ),
    clienteSolicitante: toNullableString(
      item.clienteSolicitante ?? item.cliente_solicitante
    ),
    motivoExigencia: toNullableString(
      item.motivoExigencia ?? item.motivo_exigencia
    ),

    dataEmissao,
    dataValidade,
    dataUpload,

    tipoValidade: normalizeTipoValidade(
      item.tipoValidade ?? item.tipo_validade
    ),
    periodicidade: normalizePeriodicidade(item.periodicidade),

    areaResponsavel:
      toNullableString(item.areaResponsavel ?? item.area_responsavel) ||
      "Não informada",
    responsavelNome: toNullableString(
      item.responsavelNome ?? item.responsavel_nome
    ),

    statusValidade: normalizeStatusValidade(
      item.statusValidade ?? item.status_validade
    ),

    providenciaStatus: normalizeProvidenciaStatus(
      item.providenciaStatus ??
        item.providencia_status ??
        item.renovacaoStatus ??
        item.renovacao_status ??
        item.emissaoStatus ??
        item.emissao_status
    ),
    providenciaAgendadaPara: normalizeApiDateTime(
      item.providenciaAgendadaPara ??
        item.providencia_agendada_para ??
        item.renovacaoAgendadaPara ??
        item.renovacao_agendada_para ??
        item.emissaoAgendadaPara ??
        item.emissao_agendada_para
    ),
    providenciaTipoAtendimento: normalizeProvidenciaTipoAtendimento(
      item.providenciaTipoAtendimento ??
        item.providencia_tipo_atendimento ??
        item.renovacaoTipoAtendimento ??
        item.renovacao_tipo_atendimento ??
        item.emissaoTipoAtendimento ??
        item.emissao_tipo_atendimento
    ),
    providenciaLocal: toNullableString(
      item.providenciaLocal ??
        item.providencia_local ??
        item.renovacaoLocal ??
        item.renovacao_local ??
        item.emissaoLocal ??
        item.emissao_local
    ),
    providenciaDocumentosNecessarios: toNullableString(
      item.providenciaDocumentosNecessarios ??
        item.providencia_documentos_necessarios ??
        item.renovacaoDocumentosNecessarios ??
        item.renovacao_documentos_necessarios ??
        item.emissaoDocumentosNecessarios ??
        item.emissao_documentos_necessarios
    ),
    providenciaObservacao: toNullableString(
      item.providenciaObservacao ??
        item.providencia_observacao ??
        item.renovacaoObservacao ??
        item.renovacao_observacao ??
        item.emissaoObservacao ??
        item.emissao_observacao
    ),

    anexoDisponivel:
      item.anexoDisponivel ?? item.anexo_disponivel ?? Boolean(arquivoVigenteId),
    arquivoVigenteId,
    arquivoNome: toNullableString(item.arquivoNome ?? item.arquivo_nome),
    s3Key: toNullableString(item.s3Key ?? item.s3_key),

    observacao: toNullableString(item.observacao ?? item.descricao),

    portais: {
      totalExigem: toNumber(portaisRaw.totalExigem, 0),
      anexadosAtualizados: toNumber(portaisRaw.anexadosAtualizados, 0),
      pendenteAnexo: toNumber(portaisRaw.pendenteAnexo, 0),
      vencidos: toNumber(portaisRaw.vencidos, 0),
      renovacaoPendente,
      aguardandoValidacao: toNumber(portaisRaw.aguardandoValidacao, 0),
    },
  };

  return documento;
}

function mapArquivoApi(item: DocumentoArquivoApi): DocumentoVersao {
  const id = toNumber(item.id);
  const documentoId = toNumber(item.documentoId ?? item.documento_id);
  const versao = toNumber(item.versao, 1);

  return {
    id,
    documentoId,
    versao,
    arquivoNome:
      toNullableString(item.originalName ?? item.original_name) ||
      toNullableString(item.s3Key ?? item.s3_key) ||
      `Arquivo ${id}`,
    s3Bucket: toNullableString(item.s3Bucket ?? item.s3_bucket),
    s3Key: toNullableString(item.s3Key ?? item.s3_key) || "",
    mimeType: toNullableString(item.mimeType ?? item.mime_type),
    dataEmissao: normalizeApiDate(item.dataEmissao ?? item.data_emissao),
    dataValidade: normalizeApiDate(item.dataValidade ?? item.data_validade),
    dataUpload: normalizeApiDateTime(item.criadoEm ?? item.criado_em),
    responsavelUpload:
      toNullableString(item.criadoPor ?? item.criado_por) || "Não informado",
    vigente: Boolean(item.vigente),
    obsoleto: Boolean(item.obsoleto),
    observacao: toNullableString(item.observacao),
  };
}

const DetailField: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div style={styles.fieldBox}>
    <span style={styles.fieldLabel}>{label}</span>
    <strong style={styles.fieldValue}>{value || "—"}</strong>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: string;
  helper?: string;
}> = ({ label, value, helper }) => (
  <div style={styles.statCard}>
    <span style={styles.statLabel}>{label}</span>
    <strong style={styles.statValue}>{value}</strong>
    {helper && <span style={styles.statHelper}>{helper}</span>}
  </div>
);

const StatusChip: React.FC<{
  children: React.ReactNode;
  visual: {
    background: string;
    color: string;
    border: string;
    icon?: React.ReactNode;
  };
}> = ({ children, visual }) => (
  <span
    style={{
      ...styles.chip,
      background: visual.background,
      color: visual.color,
      borderColor: visual.border,
    }}
  >
    {visual.icon}
    {children}
  </span>
);

const DocumentoViewer: React.FC<{
  viewer: ViewerState;
  departmentColor: string;
  onClose: () => void;
  onDownload: () => void;
}> = ({ viewer, departmentColor, onClose, onDownload }) => {
  if (!viewer.open) return null;

  const mime = String(viewer.mimeType || "").toLowerCase();
  const fileName = String(viewer.fileName || "").toLowerCase();

  const isPdf = mime.includes("pdf") || fileName.endsWith(".pdf");
  const isImage =
    mime.startsWith("image/") ||
    fileName.endsWith(".png") ||
    fileName.endsWith(".jpg") ||
    fileName.endsWith(".jpeg") ||
    fileName.endsWith(".webp") ||
    fileName.endsWith(".gif");

  const openInNewTab = () => {
    if (viewer.url) window.open(viewer.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div style={styles.viewerBackdrop}>
      <div style={styles.viewerModal}>
        <div style={styles.viewerHeader}>
          <div style={styles.viewerTitleBlock}>
            <strong style={styles.viewerTitle}>{viewer.fileName || "Documento"}</strong>
            <span style={styles.viewerSubtitle}>
              Visualização temporária do anexo vigente.
            </span>
          </div>

          <div style={styles.viewerActions}>
            {viewer.url && (
              <button
                type="button"
                style={{
                  ...styles.viewerActionButton,
                  color: departmentColor,
                  borderColor: toRgba(departmentColor, 0.3),
                }}
                onClick={openInNewTab}
              >
                <ExternalLink size={13} />
                Abrir em aba
              </button>
            )}

            {viewer.url && (
              <button
                type="button"
                style={{
                  ...styles.viewerActionButton,
                  color: departmentColor,
                  borderColor: toRgba(departmentColor, 0.3),
                }}
                onClick={onDownload}
              >
                <Download size={13} />
                Baixar
              </button>
            )}

            <button type="button" style={styles.viewerCloseButton} onClick={onClose}>
              <X size={17} />
            </button>
          </div>
        </div>

        <div style={styles.viewerBody}>
          {viewer.loading && (
            <div style={styles.viewerStateBox}>
              <Loader2 size={26} color={departmentColor} />
              <span>Carregando documento.</span>
            </div>
          )}

          {!viewer.loading && viewer.error && (
            <div style={styles.viewerErrorBox}>
              <AlertTriangle size={20} />
              <div>
                <strong>Não foi possível visualizar o documento.</strong>
                <span>{viewer.error}</span>
              </div>
            </div>
          )}

          {!viewer.loading && !viewer.error && viewer.url && isPdf && (
            <iframe
              src={viewer.url}
              title={viewer.fileName || "Documento PDF"}
              style={styles.viewerIframe}
            />
          )}

          {!viewer.loading && !viewer.error && viewer.url && isImage && (
            <div style={styles.viewerImageWrap}>
              <img
                src={viewer.url}
                alt={viewer.fileName || "Documento"}
                style={styles.viewerImage}
              />
            </div>
          )}

          {!viewer.loading && !viewer.error && viewer.url && !isPdf && !isImage && (
            <div style={styles.viewerStateBox}>
              <FileText size={28} color={departmentColor} />
              <strong>Pré-visualização não disponível para este formato.</strong>
              <span>Use “Abrir em aba” ou “Baixar” para acessar o arquivo.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DocumentoDetailSidePanel: React.FC<DocumentoDetailSidePanelProps> = ({
  open,
  documentoId,
  departmentColor,
  onClose,
  onAfterChange,
}) => {
  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;

  const { sessao } = useAuth() as any;

  const empresaId = useMemo(() => getEmpresaIdFromSessao(sessao), [sessao]);
  const moduloId = useMemo(() => getModuloIdFromSessao(sessao), [sessao]);

  const empresaNome =
    sessao?.empresa?.razao_social ||
    sessao?.empresa_razao_social ||
    "Empresa não informada";

  const empresaCnpj = sessao?.empresa?.cnpj || sessao?.empresa_cnpj || "";

  const idNumber = Number(documentoId);

  const [documento, setDocumento] = useState<DocumentoDetalhe | null>(null);
  const [versoes, setVersoes] = useState<DocumentoVersao[]>([]);
  const [portais, setPortais] = useState<DocumentoPortalApi[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadObservacao, setUploadObservacao] = useState("");
  const [uploadDataEmissao, setUploadDataEmissao] = useState("");
  const [uploadDataValidade, setUploadDataValidade] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadErro, setUploadErro] = useState<string | null>(null);

  const [viewer, setViewer] = useState<ViewerState>(initialViewerState);

  const versaoVigente = useMemo(() => {
    return versoes.find((item) => item.vigente) || versoes[0] || null;
  }, [versoes]);

  const rastreabilidade = useMemo<DocumentoRastreabilidade[]>(() => {
    if (!documento) return [];

    return versoes.map((versao) => {
      const statusRastreabilidade = getRastreabilidadeStatus(versao, documento);

      return {
        ...versao,
        statusRastreabilidade,
        titulo: getRastreabilidadeLabel(statusRastreabilidade),
        descricaoStatus: getRastreabilidadeDescricao(
          statusRastreabilidade,
          versao
        ),
      };
    });
  }, [documento, versoes]);

  const canUpload = Boolean(selectedFile && documento && empresaId && moduloId);

  const carregarDetalhe = async (signal?: AbortSignal) => {
    setLoading(true);
    setErro(null);

    try {
      if (!Number.isFinite(idNumber)) {
        throw new Error("documento_id inválido.");
      }

      if (!empresaId) {
        throw new Error("empresa_id não encontrado no contexto de autenticação.");
      }

      if (!moduloId) {
        throw new Error(
          "modulo_id não encontrado no contexto. Verifique o vínculo do módulo no menu/permissões."
        );
      }

      const params = new URLSearchParams();
      params.set("empresa_id", String(empresaId));
      params.set("modulo_id", String(moduloId));

      const response = await fetch(
        `${API_DOCUMENTOS_URL}/${idNumber}?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
          signal,
        }
      );

      const data = (await response
        .json()
        .catch(() => null)) as ApiDetalheResponse | null;

      if (!response.ok || !data?.ok || !data.documento) {
        throw new Error(
          data?.erro ||
            `Falha ao carregar documento. Status HTTP ${response.status}`
        );
      }

      const mappedDocumento = mapDocumentoApi(
        data.documento,
        empresaNome,
        empresaCnpj
      );

      const arquivoVigente = data.arquivoVigente || data.arquivo_vigente || null;

      const rastreabilidadeRaw = Array.isArray(data.rastreabilidade)
        ? data.rastreabilidade
        : arquivoVigente
        ? [arquivoVigente]
        : [];

      const mappedVersoes = rastreabilidadeRaw
        .map(mapArquivoApi)
        .sort((a, b) => b.versao - a.versao);

      setDocumento(mappedDocumento);
      setVersoes(mappedVersoes);
      setPortais(Array.isArray(data.portais) ? data.portais : []);
    } catch (error: any) {
      if (error?.name === "AbortError") return;

      setErro(error?.message || "Falha ao carregar documento.");
      setDocumento(null);
      setVersoes([]);
      setPortais([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setDocumento(null);
      setVersoes([]);
      setPortais([]);
      setErro(null);
      setLoading(false);
      setSelectedFile(null);
      setUploadObservacao("");
      setUploadDataEmissao("");
      setUploadDataValidade("");
      setUploadErro(null);
      setUploading(false);
      setViewer(initialViewerState);
      return;
    }

    const controller = new AbortController();
    carregarDetalhe(controller.signal);

    return () => controller.abort();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idNumber, empresaId, moduloId, empresaNome, empresaCnpj, reloadKey]);

  useEffect(() => {
    if (!open) return;

    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (viewer.open) {
          setViewer(initialViewerState);
          return;
        }

        if (!uploading) onClose();
      }
    };

    window.addEventListener("keydown", handler);

    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [open, viewer.open, uploading, onClose]);

  const gerarUrlArquivo = async (arquivoId?: number | null) => {
    if (!arquivoId) {
      throw new Error("arquivo_id inválido.");
    }

    if (!empresaId || !moduloId) {
      throw new Error("Contexto de empresa/módulo não encontrado.");
    }

    const params = new URLSearchParams();
    params.set("empresa_id", String(empresaId));
    params.set("modulo_id", String(moduloId));

    const response = await fetch(
      `${API_DOCUMENTOS_URL}/anexos/${arquivoId}/url?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const data = (await response
      .json()
      .catch(() => null)) as ApiAnexoUrlResponse | null;

    if (!response.ok || !data?.ok || !data.url) {
      throw new Error(
        data?.erro || `Falha ao gerar URL do anexo. Status HTTP ${response.status}`
      );
    }

    return data;
  };

  const handlePreviewArquivo = async (arquivoId?: number | null) => {
    const fileName =
      versaoVigente?.arquivoNome ||
      documento?.arquivoNome ||
      documento?.s3Key ||
      "Documento";

    const mimeType =
      versaoVigente?.mimeType ||
      guessMimeTypeFromName(fileName, versaoVigente?.mimeType);

    setViewer({
      open: true,
      loading: true,
      url: null,
      mimeType,
      fileName,
      error: null,
    });

    try {
      const data = await gerarUrlArquivo(arquivoId);

      const finalFileName =
        data.arquivo?.originalName ||
        data.arquivo?.original_name ||
        fileName ||
        "Documento";

      const finalMimeType =
        data.arquivo?.mimeType ||
        data.arquivo?.mime_type ||
        mimeType ||
        guessMimeTypeFromName(finalFileName, null);

      setViewer({
        open: true,
        loading: false,
        url: data.url || null,
        mimeType: finalMimeType,
        fileName: finalFileName,
        error: null,
      });
    } catch (error: any) {
      setViewer((prev) => ({
        ...prev,
        loading: false,
        error: error?.message || "Falha ao visualizar anexo.",
      }));
    }
  };

  const handleDownloadArquivo = async (arquivoId?: number | null) => {
    try {
      setErro(null);

      const data = await gerarUrlArquivo(arquivoId);

      if (!data.url) {
        throw new Error("URL temporária do anexo não retornada pela API.");
      }

      const fileName =
        data.arquivo?.originalName ||
        data.arquivo?.original_name ||
        versaoVigente?.arquivoNome ||
        documento?.arquivoNome ||
        "documento";

      const response = await fetch(data.url, {
        method: "GET",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`Falha ao baixar arquivo do S3. Status HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();

      window.setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error: any) {
      setErro(error?.message || "Falha ao baixar anexo.");
    }
  };

  const handleUpload = async () => {
    if (uploading) return;

    setUploadErro(null);

    try {
      if (!documento) {
        throw new Error("Documento não carregado.");
      }

      if (!empresaId || !moduloId) {
        throw new Error("Contexto de empresa/módulo não encontrado.");
      }

      if (!selectedFile) {
        throw new Error("Selecione um arquivo para anexar.");
      }

      if (
        documento.tipoValidade !== "sem_vencimento" &&
        documento.tipoValidade !== "sob_demanda" &&
        !String(uploadDataValidade ?? "").trim()
      ) {
        throw new Error("Informe a data de vencimento do documento.");
      }

      setUploading(true);

      const file = selectedFile;

      const presignResponse = await fetch(API_PRESIGN_UPLOAD_URL, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          modulo_id: moduloId,
          documento_id: documento.id,
          mime_type: file.type || "application/octet-stream",
          original_name: file.name,
          size_bytes: file.size,
        }),
      });

      const presignData = (await presignResponse
        .json()
        .catch(() => null)) as ApiPresignUploadResponse | null;

      const uploadUrl = presignData?.uploadUrl || presignData?.upload_url;

      if (!presignResponse.ok || !presignData?.ok || !uploadUrl) {
        throw new Error(
          presignData?.erro ||
            `Falha ao preparar upload. Status HTTP ${presignResponse.status}`
        );
      }

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(
          `Falha ao enviar arquivo ao S3. Status HTTP ${uploadResponse.status}`
        );
      }

      const registrarResponse = await fetch(API_REGISTRAR_ANEXO_URL, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          modulo_id: moduloId,
          documento_id: documento.id,
          s3_bucket: presignData.bucket,
          s3_key: presignData.key,
          mime_type: file.type || "application/octet-stream",
          original_name: file.name,
          size_bytes: file.size,
          data_emissao: uploadDataEmissao || null,
          data_validade:
            documento.tipoValidade === "sem_vencimento" ||
            documento.tipoValidade === "sob_demanda"
              ? null
              : uploadDataValidade || null,
          observacao: String(uploadObservacao ?? "").trim() || null,

          documento_possuido: true,
          providencia_status: documento.documentoPossuido
            ? "concluida"
            : "emitido",
          emissao_status: documento.documentoPossuido ? null : "emitido",
          renovacao_status: documento.documentoPossuido ? "concluida" : null,
        }),
      });

      const registrarData = (await registrarResponse
        .json()
        .catch(() => null)) as ApiRegistrarAnexoResponse | null;

      if (!registrarResponse.ok || !registrarData?.ok) {
        throw new Error(
          registrarData?.erro ||
            `Falha ao registrar anexo. Status HTTP ${registrarResponse.status}`
        );
      }

      setSelectedFile(null);
      setUploadObservacao("");
      setUploadDataEmissao("");
      setUploadDataValidade("");
      setReloadKey((prev) => prev + 1);
      onAfterChange?.();
    } catch (error: any) {
      setUploadErro(error?.message || "Falha ao anexar documento.");
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  const validadeVisual = documento
    ? getStatusValidadeVisual(documento.statusValidade)
    : getStatusValidadeVisual("sem_validade");

  const operacionalVisual = documento
    ? getOperacionalVisual(documento.portais)
    : getOperacionalVisual({
        totalExigem: 0,
        anexadosAtualizados: 0,
        pendenteAnexo: 0,
        vencidos: 0,
        renovacaoPendente: 0,
        aguardandoValidacao: 0,
      });

  const providenciaVisual = documento
    ? getProvidenciaVisual(documento.providenciaStatus)
    : getProvidenciaVisual("sem_agendamento");

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Detalhes do documento"
        style={styles.backdrop}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !uploading) {
            onClose();
          }
        }}
      >
        <aside
          style={styles.panel}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header
            style={{
              ...styles.header,
              borderTop: `3px solid ${activeDepartmentColor}`,
            }}
          >
            <div style={styles.headerText}>
              <span style={styles.eyebrow}>Governança documental</span>

              <h2 style={styles.title}>
                {loading
                  ? "Carregando documento"
                  : documento?.nome || "Documento não encontrado"}
              </h2>

              <p style={styles.subtitle}>
                {documento
                  ? `${documento.categoria} · ${documento.orgaoEmissor}`
                  : "Consulta detalhada do documento selecionado na listagem."}
              </p>
            </div>

            <div style={styles.headerActions}>
              {documento?.arquivoVigenteId && (
                <button
                  type="button"
                  style={{
                    ...styles.primaryButton,
                    background: activeDepartmentColor,
                    borderColor: activeDepartmentColor,
                  }}
                  onClick={() => handlePreviewArquivo(documento.arquivoVigenteId)}
                >
                  <ExternalLink size={14} />
                  Ver anexo
                </button>
              )}

              <button type="button" style={styles.closeButton} onClick={onClose}>
                <X size={18} />
              </button>
            </div>
          </header>

          <main style={styles.content}>
            {loading && (
              <section style={styles.emptyPanel}>
                <Loader2 size={26} color={activeDepartmentColor} />
                <div>
                  <h3 style={styles.emptyTitle}>Carregando documento</h3>
                  <p style={styles.emptyText}>
                    Consultando dados de governança documental.
                  </p>
                </div>
              </section>
            )}

            {!loading && erro && (
              <section style={styles.errorPanel}>
                <AlertTriangle size={22} />
                <div>
                  <strong>Não foi possível carregar o documento.</strong>
                  <span>{erro}</span>
                  <button
                    type="button"
                    style={styles.retryButton}
                    onClick={() => setReloadKey((prev) => prev + 1)}
                  >
                    Tentar novamente
                  </button>
                </div>
              </section>
            )}

            {!loading && !erro && documento && (
              <>
                <section style={styles.statsGrid}>
                  <StatCard
                    label="Validade"
                    value={formatNullableDate(documento.dataValidade)}
                    helper={getPrazoOperacional(documento)}
                  />

                  <StatCard
                    label="Status"
                    value={getStatusValidadeLabel(documento.statusValidade)}
                    helper={getTipoValidadeLabel(documento.tipoValidade)}
                  />

                  <StatCard
                    label="Portais"
                    value={`${documento.portais.anexadosAtualizados}/${documento.portais.totalExigem}`}
                    helper={operacionalVisual.label}
                  />

                  <StatCard
                    label="Anexo vigente"
                    value={documento.anexoDisponivel ? "Disponível" : "Pendente"}
                    helper={documento.arquivoNome || documento.s3Key || "Sem arquivo vigente"}
                  />
                </section>

                <section style={styles.section}>
                  <div style={styles.sectionHeader}>
                    <div>
                      <h3 style={styles.sectionTitle}>Resumo documental</h3>
                      <p style={styles.sectionSubtitle}>
                        Dados principais, regra de validade e situação atual.
                      </p>
                    </div>

                    <div style={styles.chipRow}>
                      <StatusChip visual={validadeVisual}>
                        {validadeVisual.icon}
                        {getStatusValidadeLabel(documento.statusValidade)}
                      </StatusChip>

                      <StatusChip visual={operacionalVisual}>
                        {operacionalVisual.label}
                      </StatusChip>
                    </div>
                  </div>

                  <div style={styles.fieldGrid}>
                    <DetailField label="Documento" value={documento.nome} />
                    <DetailField label="Categoria" value={documento.categoria} />
                    <DetailField label="Órgão emissor" value={documento.orgaoEmissor} />
                    <DetailField label="Empresa" value={documento.empresa} />
                    <DetailField label="CNPJ" value={documento.cnpj || "Não informado"} />
                    <DetailField
                      label="Documento possuído"
                      value={documento.documentoPossuido ? "Sim" : "Ainda não"}
                    />
                    <DetailField
                      label="Origem da solicitação"
                      value={getOrigemSolicitacaoLabel(documento.origemSolicitacao)}
                    />
                    <DetailField
                      label="Cliente solicitante"
                      value={documento.clienteSolicitante || "Não informado"}
                    />
                    <DetailField
                      label="Data de emissão"
                      value={formatNullableDate(documento.dataEmissao)}
                    />
                    <DetailField
                      label="Data de validade"
                      value={formatNullableDate(documento.dataValidade)}
                    />
                    <DetailField
                      label="Tipo de validade"
                      value={getTipoValidadeLabel(documento.tipoValidade)}
                    />
                    <DetailField
                      label="Periodicidade"
                      value={getPeriodicidadeLabel(documento.periodicidade)}
                    />
                    <DetailField
                      label="Área responsável"
                      value={documento.areaResponsavel}
                    />
                    <DetailField
                      label="Responsável"
                      value={documento.responsavelNome || "Não informado"}
                    />
                  </div>

                  {(documento.motivoExigencia || documento.observacao) && (
                    <div style={styles.noteGrid}>
                      {documento.motivoExigencia && (
                        <div style={styles.noteBox}>
                          <span style={styles.noteLabel}>Motivo da exigência</span>
                          <p style={styles.noteText}>{documento.motivoExigencia}</p>
                        </div>
                      )}

                      {documento.observacao && (
                        <div style={styles.noteBox}>
                          <span style={styles.noteLabel}>Observação</span>
                          <p style={styles.noteText}>{documento.observacao}</p>
                        </div>
                      )}
                    </div>
                  )}
                </section>

                <section style={styles.section}>
                  <div style={styles.sectionHeader}>
                    <div>
                      <h3 style={styles.sectionTitle}>Providência</h3>
                      <p style={styles.sectionSubtitle}>
                        Acompanhamento de emissão, renovação ou regularização.
                      </p>
                    </div>

                    <StatusChip visual={providenciaVisual}>
                      {getProvidenciaStatusLabel(
                        documento.providenciaStatus,
                        documento.documentoPossuido
                      )}
                    </StatusChip>
                  </div>

                  <div style={styles.fieldGrid}>
                    <DetailField
                      label="Status da providência"
                      value={getProvidenciaStatusLabel(
                        documento.providenciaStatus,
                        documento.documentoPossuido
                      )}
                    />
                    <DetailField
                      label="Agendada para"
                      value={documento.providenciaAgendadaPara || "Não informado"}
                    />
                    <DetailField
                      label="Tipo de atendimento"
                      value={getProvidenciaTipoAtendimentoLabel(
                        documento.providenciaTipoAtendimento
                      )}
                    />
                    <DetailField
                      label="Local / Canal"
                      value={documento.providenciaLocal || "Não informado"}
                    />
                    <DetailField
                      label="Documentos necessários"
                      value={
                        documento.providenciaDocumentosNecessarios ||
                        "Não informado"
                      }
                    />
                    <DetailField
                      label="Observação da providência"
                      value={documento.providenciaObservacao || "Não informado"}
                    />
                  </div>
                </section>

                <section style={styles.section}>
                  <div style={styles.sectionHeader}>
                    <div>
                      <h3 style={styles.sectionTitle}>Anexo vigente</h3>
                      <p style={styles.sectionSubtitle}>
                        Arquivo atual vinculado ao documento.
                      </p>
                    </div>

                    <Paperclip size={18} color={activeDepartmentColor} />
                  </div>

                  {versaoVigente ? (
                    <div
                      style={{
                        ...styles.fileCard,
                        borderColor: toRgba(activeDepartmentColor, 0.28),
                        boxShadow: `inset 4px 0 0 ${activeDepartmentColor}`,
                      }}
                    >
                      <div style={styles.fileInfo}>
                        <strong style={styles.fileName}>
                          {versaoVigente.arquivoNome}
                        </strong>

                        <span style={styles.fileMeta}>
                          Versão {versaoVigente.versao} · Upload em{" "}
                          {versaoVigente.dataUpload}
                        </span>

                        <span style={styles.fileMeta}>
                          Emissão: {formatNullableDate(versaoVigente.dataEmissao)} ·
                          Validade: {formatNullableDate(versaoVigente.dataValidade)}
                        </span>
                      </div>

                      <div style={styles.fileActions}>
                        <button
                          type="button"
                          style={{
                            ...styles.secondaryButton,
                            color: activeDepartmentColor,
                            borderColor: toRgba(activeDepartmentColor, 0.32),
                          }}
                          onClick={() => handlePreviewArquivo(versaoVigente.id)}
                        >
                          <ExternalLink size={13} />
                          Visualizar
                        </button>

                        <button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() => handleDownloadArquivo(versaoVigente.id)}
                        >
                          <Download size={13} />
                          Baixar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={styles.emptyInline}>
                      Nenhum anexo vigente registrado para este documento.
                    </div>
                  )}

                  <div style={styles.uploadBox}>
                    <div style={styles.uploadHeader}>
                      <UploadCloud size={17} color={activeDepartmentColor} />
                      <div>
                        <strong>Anexar nova versão</strong>
                        <span>
                          O envio registrará uma nova evidência e atualizará a
                          rastreabilidade.
                        </span>
                      </div>
                    </div>

                    <div style={styles.uploadGrid}>
                      <label style={styles.inputLabel}>
                        Arquivo
                        <input
                          type="file"
                          style={styles.fileInput}
                          onChange={(event) =>
                            setSelectedFile(event.target.files?.[0] || null)
                          }
                        />
                      </label>

                      <label style={styles.inputLabel}>
                        Data de emissão
                        <input
                          type="date"
                          value={uploadDataEmissao}
                          style={styles.input}
                          onChange={(event) =>
                            setUploadDataEmissao(event.target.value)
                          }
                        />
                      </label>

                      <label style={styles.inputLabel}>
                        Data de validade
                        <input
                          type="date"
                          value={uploadDataValidade}
                          style={styles.input}
                          disabled={
                            documento.tipoValidade === "sem_vencimento" ||
                            documento.tipoValidade === "sob_demanda"
                          }
                          onChange={(event) =>
                            setUploadDataValidade(event.target.value)
                          }
                        />
                      </label>

                      <label style={styles.inputLabel}>
                        Observação
                        <input
                          type="text"
                          value={uploadObservacao}
                          style={styles.input}
                          placeholder="Ex.: Renovação enviada ao portal"
                          onChange={(event) =>
                            setUploadObservacao(event.target.value)
                          }
                        />
                      </label>
                    </div>

                    {selectedFile && (
                      <div style={styles.selectedFile}>
                        Selecionado: <strong>{selectedFile.name}</strong>
                      </div>
                    )}

                    {uploadErro && <div style={styles.uploadError}>{uploadErro}</div>}

                    <div style={styles.uploadFooter}>
                      <button
                        type="button"
                        style={{
                          ...styles.primaryButton,
                          background: activeDepartmentColor,
                          borderColor: activeDepartmentColor,
                          opacity: canUpload && !uploading ? 1 : 0.58,
                        }}
                        disabled={!canUpload || uploading}
                        onClick={handleUpload}
                      >
                        {uploading ? <Loader2 size={14} /> : <UploadCloud size={14} />}
                        {uploading ? "Enviando..." : "Enviar nova versão"}
                      </button>
                    </div>
                  </div>
                </section>

                <section style={styles.section}>
                  <div style={styles.sectionHeader}>
                    <div>
                      <h3 style={styles.sectionTitle}>Situação nos portais</h3>
                      <p style={styles.sectionSubtitle}>
                        Consolidação operacional por portal/cliente.
                      </p>
                    </div>
                  </div>

                  {portais.length ? (
                    <div style={styles.portalList}>
                      {portais.map((portal) => {
                        const status =
                          portal.statusOperacional ?? portal.status_operacional;
                        const visual = getPortalStatusVisual(status);
                        const cliente =
                          portal.clienteNome ??
                          portal.cliente_nome ??
                          "Cliente não informado";
                        const portalNome =
                          portal.portalNome ??
                          portal.portal_nome ??
                          "Portal não informado";
                        const portalUrl = portal.portalUrl ?? portal.portal_url;

                        return (
                          <div key={String(portal.id)} style={styles.portalCard}>
                            <div style={styles.portalTop}>
                              <div style={styles.portalTitleArea}>
                                <strong style={styles.portalTitle}>{cliente}</strong>
                                <span style={styles.portalSubtitle}>
                                  {portalNome}
                                </span>
                              </div>

                              <StatusChip visual={visual}>
                                {getPortalStatusLabel(status)}
                              </StatusChip>
                            </div>

                            <div style={styles.portalMetaGrid}>
                              <DetailField
                                label="Exige documento"
                                value={
                                  portal.exigeDocumento ??
                                  portal.exige_documento
                                    ? "Sim"
                                    : "Não"
                                }
                              />
                              <DetailField
                                label="Atualizado em"
                                value={normalizeApiDateTime(
                                  portal.atualizadoEm ?? portal.atualizado_em
                                )}
                              />
                              <DetailField
                                label="Arquivo vinculado"
                                value={portal.arquivoId ?? portal.arquivo_id ?? "—"}
                              />
                              <DetailField
                                label="Observação"
                                value={portal.observacao || "—"}
                              />
                            </div>

                            {portalUrl && (
                              <button
                                type="button"
                                style={styles.portalLink}
                                onClick={() =>
                                  window.open(
                                    portalUrl,
                                    "_blank",
                                    "noopener,noreferrer"
                                  )
                                }
                              >
                                <ExternalLink size={13} />
                                Abrir portal
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={styles.emptyInline}>
                      Nenhum portal vinculado a este documento.
                    </div>
                  )}
                </section>

                <section style={styles.section}>
                  <div style={styles.sectionHeader}>
                    <div>
                      <h3 style={styles.sectionTitle}>Rastreabilidade documental</h3>
                      <p style={styles.sectionSubtitle}>
                        Histórico de versões anexadas ao documento.
                      </p>
                    </div>
                  </div>

                  {rastreabilidade.length ? (
                    <div style={styles.timeline}>
                      {rastreabilidade.map((versao) => {
                        const visual = getRastreabilidadeVisual(
                          versao.statusRastreabilidade
                        );

                        return (
                          <div
                            key={versao.id}
                            style={{
                              ...styles.timelineItem,
                              background: visual.background,
                              borderColor: visual.border,
                            }}
                          >
                            <div
                              style={{
                                ...styles.timelineDot,
                                background: visual.color,
                              }}
                            />

                            <div style={styles.timelineBody}>
                              <div style={styles.timelineTop}>
                                <strong style={styles.timelineTitle}>
                                  Versão {versao.versao} · {versao.titulo}
                                </strong>

                                <span
                                  style={{
                                    ...styles.timelineBadge,
                                    color: visual.color,
                                    borderColor: visual.border,
                                  }}
                                >
                                  {versao.vigente ? "Vigente" : "Histórico"}
                                </span>
                              </div>

                              <span style={styles.timelineDescription}>
                                {versao.descricaoStatus}
                              </span>

                              <div style={styles.timelineMetaGrid}>
                                <DetailField
                                  label="Arquivo"
                                  value={versao.arquivoNome}
                                />
                                <DetailField
                                  label="Upload"
                                  value={versao.dataUpload}
                                />
                                <DetailField
                                  label="Emissão"
                                  value={formatNullableDate(versao.dataEmissao)}
                                />
                                <DetailField
                                  label="Validade"
                                  value={formatNullableDate(versao.dataValidade)}
                                />
                              </div>

                              {versao.observacao && (
                                <p style={styles.timelineObservation}>
                                  {versao.observacao}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={styles.emptyInline}>
                      Nenhuma versão registrada para este documento.
                    </div>
                  )}
                </section>
              </>
            )}
          </main>
        </aside>
      </div>

      <DocumentoViewer
        viewer={viewer}
        departmentColor={activeDepartmentColor}
        onClose={() => setViewer(initialViewerState)}
        onDownload={() =>
          handleDownloadArquivo(versaoVigente?.id || documento?.arquivoVigenteId)
        }
      />
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    top: HEADER_HEIGHT,
    left: LEFTBAR_WIDTH,
    right: 0,
    bottom: 0,
    zIndex: 1190,
    display: "flex",
    justifyContent: "flex-end",
    background:
      "radial-gradient(circle at top left, rgba(47, 63, 89, 0.10), transparent 34%), rgba(15, 23, 42, 0.28)",
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
    boxSizing: "border-box",
  },

  panel: {
    width: `min(1080px, calc(100vw - ${LEFTBAR_WIDTH}px))`,
    height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    background: "#ffffff",
    borderLeft: "1px solid #d9e2ec",
    boxShadow: "-18px 0 46px rgba(15, 23, 42, 0.22)",
    display: "flex",
    flexDirection: "column",
    fontFamily:
      'Nunito, Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: "#0f172a",
  },

  header: {
    padding: "17px 20px 16px",
    borderBottom: "1px solid #e5eaf0",
    background:
      "linear-gradient(135deg, rgba(248, 250, 252, 0.98), rgba(255, 255, 255, 1))",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },

  headerText: {
    minWidth: 0,
  },

  eyebrow: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.42,
    textTransform: "uppercase",
  },

  title: {
    margin: "6px 0 4px",
    color: "#0f172a",
    fontSize: 21,
    fontWeight: 950,
    lineHeight: 1.18,
    letterSpacing: "-0.02em",
    overflowWrap: "anywhere",
  },

  subtitle: {
    margin: 0,
    maxWidth: 780,
    color: "#64748b",
    fontSize: 12.5,
    fontWeight: 760,
    lineHeight: 1.45,
  },

  headerActions: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },

  primaryButton: {
    minHeight: 36,
    border: "1px solid #2f3f59",
    background: "#2f3f59",
    color: "#ffffff",
    padding: "0 13px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    whiteSpace: "nowrap",
  },

  secondaryButton: {
    minHeight: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "0 12px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    whiteSpace: "nowrap",
  },

  closeButton: {
    width: 36,
    height: 36,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  content: {
    flex: 1,
    overflowY: "auto",
    background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  emptyPanel: {
    border: "1px solid #dbe4ef",
    background: "#ffffff",
    padding: 20,
    display: "flex",
    alignItems: "center",
    gap: 14,
  },

  emptyTitle: {
    margin: "0 0 4px",
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 950,
  },

  emptyText: {
    margin: 0,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 760,
  },

  errorPanel: {
    border: "1px solid #fecaca",
    background: "linear-gradient(180deg, #fffafa, #fff7f7)",
    color: "#991b1b",
    padding: 15,
    display: "flex",
    gap: 12,
  },

  retryButton: {
    marginTop: 9,
    minHeight: 32,
    border: "1px solid #fecaca",
    background: "#ffffff",
    color: "#991b1b",
    padding: "0 12px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
  },

  statCard: {
    border: "1px solid #e2e8f0",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    padding: "13px 13px 12px",
    minHeight: 86,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    boxShadow: "0 1px 0 rgba(15, 23, 42, 0.03)",
  },

  statLabel: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
  },

  statValue: {
    fontSize: 18,
    lineHeight: 1.15,
    color: "#0f172a",
    fontWeight: 950,
    overflowWrap: "anywhere",
  },

  statHelper: {
    fontSize: 11,
    color: "#64748b",
    fontWeight: 760,
    lineHeight: 1.25,
    overflowWrap: "anywhere",
  },

  section: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: 13,
    display: "flex",
    flexDirection: "column",
    gap: 11,
    boxShadow: "0 1px 0 rgba(15, 23, 42, 0.03)",
  },

  sectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },

  sectionTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 13.5,
    fontWeight: 950,
  },

  sectionSubtitle: {
    margin: "3px 0 0",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 760,
    lineHeight: 1.35,
  },

  chipRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 7,
    flexWrap: "wrap",
  },

  chip: {
    minHeight: 25,
    padding: "0 9px",
    border: "1px solid #cbd5e1",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    fontSize: 11.5,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },

  fieldBox: {
    border: "1px solid #edf2f7",
    background: "#f8fafc",
    padding: "9px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    minWidth: 0,
  },

  fieldLabel: {
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
  },

  fieldValue: {
    color: "#0f172a",
    fontSize: 12.5,
    fontWeight: 900,
    overflowWrap: "anywhere",
  },

  noteGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  },

  noteBox: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: 10,
  },

  noteLabel: {
    display: "block",
    color: "#64748b",
    fontSize: 11,
    fontWeight: 900,
    marginBottom: 4,
  },

  noteText: {
    margin: 0,
    color: "#334155",
    fontSize: 12.5,
    lineHeight: 1.45,
    fontWeight: 760,
  },

  fileCard: {
    border: "1px solid #dbe4ef",
    background: "#ffffff",
    padding: 12,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },

  fileInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },

  fileName: {
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 950,
    overflowWrap: "anywhere",
  },

  fileMeta: {
    color: "#64748b",
    fontSize: 11.5,
    fontWeight: 760,
    lineHeight: 1.35,
  },

  fileActions: {
    display: "flex",
    gap: 7,
    flexShrink: 0,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },

  uploadBox: {
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  uploadHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
  },

  uploadGrid: {
    display: "grid",
    gridTemplateColumns: "1.2fr repeat(3, minmax(0, 1fr))",
    gap: 8,
  },

  inputLabel: {
    color: "#475569",
    fontSize: 11.5,
    fontWeight: 900,
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },

  input: {
    height: 36,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    padding: "0 10px",
    fontSize: 12.5,
    fontWeight: 760,
    fontFamily: "inherit",
    outline: "none",
  },

  fileInput: {
    minHeight: 36,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    padding: "7px 10px",
    fontSize: 12,
    fontWeight: 760,
    fontFamily: "inherit",
  },

  selectedFile: {
    color: "#475569",
    fontSize: 12,
    fontWeight: 760,
  },

  uploadError: {
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    padding: "8px 10px",
    fontSize: 12,
    fontWeight: 850,
  },

  uploadFooter: {
    display: "flex",
    justifyContent: "flex-end",
  },

  portalList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  portalCard: {
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 9,
  },

  portalTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
  },

  portalTitleArea: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  portalTitle: {
    color: "#0f172a",
    fontSize: 12.5,
    fontWeight: 950,
    overflowWrap: "anywhere",
  },

  portalSubtitle: {
    color: "#64748b",
    fontSize: 11.5,
    fontWeight: 760,
  },

  portalMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 7,
  },

  portalLink: {
    width: "fit-content",
    minHeight: 30,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "0 10px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },

  timeline: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  timelineItem: {
    position: "relative",
    border: "1px solid #e2e8f0",
    padding: "10px 10px 10px 26px",
    display: "flex",
    gap: 10,
  },

  timelineDot: {
    position: "absolute",
    left: 10,
    top: 15,
    width: 9,
    height: 9,
    borderRadius: 999,
  },

  timelineBody: {
    minWidth: 0,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  timelineTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
  },

  timelineTitle: {
    color: "#0f172a",
    fontSize: 12.5,
    fontWeight: 950,
    overflowWrap: "anywhere",
  },

  timelineBadge: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    padding: "3px 8px",
    fontSize: 11,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  timelineDescription: {
    color: "#475569",
    fontSize: 11.8,
    fontWeight: 760,
    lineHeight: 1.35,
  },

  timelineMetaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 7,
  },

  timelineObservation: {
    margin: 0,
    border: "1px solid rgba(226, 232, 240, 0.92)",
    background: "rgba(255, 255, 255, 0.72)",
    color: "#475569",
    padding: "8px 9px",
    fontSize: 11.5,
    fontWeight: 760,
    lineHeight: 1.35,
  },

  emptyInline: {
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    color: "#64748b",
    padding: 12,
    fontSize: 12.5,
    fontWeight: 850,
  },

  viewerBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1700,
    background: "rgba(15, 23, 42, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },

  viewerModal: {
    width: "min(1120px, calc(100vw - 48px))",
    height: "min(780px, calc(100vh - 48px))",
    background: "#ffffff",
    border: "1px solid #dbe4ef",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.30)",
    display: "flex",
    flexDirection: "column",
  },

  viewerHeader: {
    padding: "12px 14px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },

  viewerTitleBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },

  viewerTitle: {
    color: "#0f172a",
    fontSize: 13.5,
    fontWeight: 950,
    overflowWrap: "anywhere",
  },

  viewerSubtitle: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 760,
  },

  viewerActions: {
    display: "flex",
    gap: 7,
    alignItems: "center",
    flexShrink: 0,
  },

  viewerActionButton: {
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    padding: "0 10px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },

  viewerCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  viewerBody: {
    flex: 1,
    background: "#f8fafc",
    overflow: "hidden",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
  },

  viewerStateBox: {
    flex: 1,
    color: "#475569",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 800,
    textAlign: "center",
    padding: 24,
  },

  viewerErrorBox: {
    margin: "auto",
    maxWidth: 520,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    padding: 14,
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },

  viewerIframe: {
    width: "100%",
    height: "100%",
    border: 0,
    background: "#ffffff",
  },

  viewerImageWrap: {
    width: "100%",
    height: "100%",
    overflow: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },

  viewerImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
};

export default DocumentoDetailSidePanel;